import { useState } from "react";
import {
  Monitor,
  Smartphone,
  Copy,
  Check,
  ExternalLink,
  BookOpen,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  AlertTriangle,
} from "lucide-react";
import { Link } from "react-router-dom";

const COUNCIL_URL =
  "https://yourvoicebucks.citizenspace.com/planning/2aa4b313/consultation/";

const DESKTOP_STEPS = [
  {
    title: "Generate your bookmarklet",
    detail:
      'Visit the home page, optionally enter your street name and years in Aylesbury, select your preferred tone, review the pre-filled responses, then click "Create Bookmarklet".',
  },
  {
    title: "Save the bookmarklet to your browser",
    detail:
      'Drag the green "AHT Fill" button to your bookmarks bar. If your bookmarks bar is hidden, press Ctrl+Shift+B (Windows) or Cmd+Shift+B (Mac) to show it. Alternatively, click "Copy Code", create a new bookmark, and paste the code as the URL.',
  },
  {
    title: "IMPORTANT: Open the council form in a NEW TAB",
    detail:
      'Right-click the council form link and select "Open link in new tab" (or Ctrl+Click). Do NOT navigate away from the AHT page — keep it open in the original tab. You will need to return to it!',
    link: COUNCIL_URL,
    important: true,
  },
  {
    title: 'Select "As an individual resident"',
    detail:
      'On the first page of the council form (in the new tab), choose "As an individual resident" and click Continue.',
  },
  {
    title: "Click your AHT Fill bookmark ONCE",
    detail:
      'On the site response page, click the "AHT Fill" bookmark. The form fills automatically and a floating status bar appears showing a countdown timer. When the countdown reaches zero, it auto-submits and moves to the next site.',
  },
  {
    title: "Sit back and watch",
    detail:
      'The bookmarklet handles everything — it fills each form, waits a short delay, submits, and moves to the next site automatically. You can use the Pause button to stop and review, or Cancel to stop entirely.',
  },
  {
    title: "Click Submit on the final form",
    detail:
      'When all sites are filled, the status bar will show "All Sites Filled!". Review the final form on the council website, then click the council\'s Submit button to submit your last response.',
  },
  {
    title: 'Return to the AHT page tab and confirm',
    detail:
      'Switch back to the AHT page tab (where you created the bookmarklet) and click the green "I\'ve Completed All My Submissions" button. This confirms your submission is complete.',
    important: true,
  },
];

const MOBILE_STEPS = [
  {
    title: "Generate your bookmarklet",
    detail:
      'Visit the home page on your mobile browser (Safari on iPhone, Chrome on Android). Enter your details, review responses, and tap "Create Bookmarklet". KEEP THIS PAGE OPEN.',
  },
  {
    title: 'Copy the bookmarklet code',
    detail:
      'Tap the "Copy Code" button. The JavaScript code will be copied to your clipboard.',
  },
  {
    title: "Create a bookmark with the code",
    detail:
      "Bookmark any page first (e.g. this page). Then go to your bookmarks, find the one you just saved, and edit it. Replace the URL with the code you copied. Rename the bookmark to \"AHT Fill\".",
    substeps: [
      "iPhone Safari: Tap the Share icon > Add Bookmark > Save. Then go to Bookmarks, tap Edit, find the bookmark, tap it, clear the URL field, paste the code, and tap Done.",
      "Android Chrome: Tap the three dots menu > Bookmark this page. Then go to Bookmarks, long-press the bookmark, tap Edit, clear the URL field, paste the code, and tap the back arrow to save.",
    ],
  },
  {
    title: "IMPORTANT: Open the council form in a NEW TAB",
    detail: "Long-press the council link and select 'Open in New Tab'. Keep the AHT page open — you need to return to it!",
    link: COUNCIL_URL,
    important: true,
  },
  {
    title: 'Select "As an individual resident" and continue',
    detail: 'Choose "As an individual resident" on the first page and tap Continue.',
  },
  {
    title: "Run the bookmarklet ONCE",
    detail:
      'On the site response page, tap your browser\'s address bar and start typing "Aylesbury Hindu Temple". Tap the bookmark from suggestions. The form fills and auto-submits after a countdown. It handles all remaining sites automatically.',
    substeps: [
      "iPhone Safari: Tap the address bar, start typing \"AHT\", and tap the bookmark from the suggestions.",
      "Android Chrome: Tap the address bar, type \"AHT\", and select the bookmark from suggestions.",
    ],
  },
  {
    title: "Watch it run",
    detail:
      'The status bar shows progress and a countdown for each site. Use Pause to stop and review, or Cancel to stop entirely.',
  },
  {
    title: "Tap Submit on the final form",
    detail:
      'When all sites are filled, the status bar will show "All Sites Filled!". Review the final form on the council website, then tap the council\'s Submit button.',
  },
  {
    title: 'Switch back to the AHT page tab and confirm',
    detail:
      'Switch to the AHT page tab (where you created the bookmarklet) and tap the green "I\'ve Completed All My Submissions" button. This confirms your submission is complete.',
    important: true,
  },
];

function StepList({ steps }) {
  return (
    <div className="guide-steps">
      {steps.map((step, i) => (
        <div 
          key={i} 
          className={`guide-step ${step.important ? 'guide-step-important' : ''}`} 
          data-testid={`guide-step-${i}`}
          style={step.important ? { 
            background: 'rgba(34, 197, 94, 0.1)', 
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '8px'
          } : {}}
        >
          <div className="guide-step-num" style={step.important ? { background: '#22c55e' } : {}}>{i + 1}</div>
          <div className="guide-step-body">
            <strong>{step.title}</strong>
            {step.important && <span style={{ marginLeft: 8, color: '#22c55e', fontSize: '0.75rem' }}>⚠️ IMPORTANT</span>}
            <p>{step.detail}</p>
            {step.link && (
              <a
                href={step.link}
                target="_blank"
                rel="noopener noreferrer"
                className="guide-link"
              >
                Open Council Form <ExternalLink size={12} />
              </a>
            )}
            {step.substeps && (
              <ul className="guide-substeps">
                {step.substeps.map((sub, j) => (
                  <li key={j}>{sub}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SupporterGuide() {
  const [tab, setTab] = useState("desktop");
  const [copied, setCopied] = useState(false);

  const plainTextInstructions = `
CIVICFLOW — How to Submit Your Responses
==========================================

Buckinghamshire Council Local Plan — Sites Survey
Supporting the Hindu community's representation.

STEP-BY-STEP (Desktop / Laptop)
---------------------------------
${DESKTOP_STEPS.map(
    (s, i) => `${i + 1}. ${s.title}\n   ${s.detail}${s.link ? `\n   Link: ${s.link}` : ""}`
  ).join("\n\n")}

STEP-BY-STEP (Mobile / Phone)
-------------------------------
${MOBILE_STEPS.map(
    (s, i) =>
      `${i + 1}. ${s.title}\n   ${s.detail}${s.link ? `\n   Link: ${s.link}` : ""}${
        s.substeps ? "\n   " + s.substeps.map((sub) => `- ${sub}`).join("\n   ") : ""
      }`
  ).join("\n\n")}

START HERE: ${window.location.origin}/

Council Form: ${COUNCIL_URL}
`.trim();

  const handleCopyInstructions = async () => {
    try {
      await navigator.clipboard.writeText(plainTextInstructions);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = plainTextInstructions;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="public-page" data-testid="supporter-guide">
      <header className="public-header">
        <div className="public-header-inner">
          <div className="guide-header-row">
            <div className="public-logo">
              <img src="/aht_round_orange.png" alt="AHT" style={{ height: 36, width: 36, borderRadius: "50%" }} />
              <span className="dash-logo-text">Aylesbury Hindu Temple</span>
            </div>
            <Link to="/" className="guide-back-link" data-testid="guide-back-link">
              <ArrowLeft size={14} />
              Back to Generator
            </Link>
          </div>
        </div>
      </header>

      <main className="public-main">
        <div className="public-hero">
          <h1 className="public-title">Supporter Guide</h1>
          <p className="public-subtitle">
            Step-by-step instructions for submitting your responses to the
            Buckinghamshire Council Local Plan consultation. Follow the guide
            for your device below.
          </p>
        </div>

        <div className="guide-important" data-testid="guide-important">
          <AlertTriangle size={18} />
          <div>
            <strong>Important — Submit from your own device</strong>
            <p>
              Each response should be submitted from the supporter's own device
              and IP address. Do not submit on behalf of others. This ensures
              each response is genuine and unique.
            </p>
          </div>
        </div>

        {/* Device Tabs */}
        <div className="guide-tabs" data-testid="guide-tabs">
          <button
            className={`guide-tab ${tab === "desktop" ? "active" : ""}`}
            onClick={() => setTab("desktop")}
            data-testid="guide-tab-desktop"
          >
            <Monitor size={16} />
            <span>Desktop / Laptop</span>
          </button>
          <button
            className={`guide-tab ${tab === "mobile" ? "active" : ""}`}
            onClick={() => setTab("mobile")}
            data-testid="guide-tab-mobile"
          >
            <Smartphone size={16} />
            <span>Mobile / Tablet</span>
          </button>
        </div>

        {/* Steps */}
        {tab === "desktop" && <StepList steps={DESKTOP_STEPS} />}
        {tab === "mobile" && <StepList steps={MOBILE_STEPS} />}

        {/* Copy All Instructions */}
        <div className="guide-share-section" data-testid="guide-share-section">
          <div className="guide-share-inner">
            <BookOpen size={20} />
            <div>
              <strong>Share these instructions</strong>
              <p>
                Copy the full instructions as plain text to share via
                WhatsApp, email, or any messaging app.
              </p>
            </div>
          </div>
          <button
            className="btn-primary"
            onClick={handleCopyInstructions}
            data-testid="guide-copy-instructions-btn"
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            <span>{copied ? "Copied!" : "Copy Instructions"}</span>
          </button>
        </div>

        {/* Quick Start Link */}
        <div className="guide-quick-start">
          <h3>Ready to start?</h3>
          <p>
            Visit the generator page to create your personalised responses and
            bookmarklet.
          </p>
          <Link to="/" className="btn-primary gen-btn" data-testid="guide-start-link">
            Go to Generator
          </Link>
        </div>

        <div style={{ textAlign: "center", marginTop: "32px", padding: "16px 0", borderTop: "1px solid var(--cf-card-border)", color: "var(--cf-muted)", fontSize: 13 }}>
          <p style={{ margin: 0 }}>
            Need further help? Contact <a href="https://www.aylesburyhindutemple.org" target="_blank" rel="noopener noreferrer" style={{ color: "var(--cf-orange)" }}>Aylesbury Hindu Temple &amp; Trust</a>
          </p>
          <p style={{ margin: "8px 0 0", fontSize: 11, opacity: 0.7 }}>
            Built by <a href="https://scaleright.ai/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--cf-orange)" }}>ScaleRight.ai</a>
          </p>
        </div>
      </main>
    </div>
  );
}
